import { getRetryDelay, logError, logInfo, sleep } from '@utils-global';
import { AxiosError } from 'axios';
import { handleCaeChallenge } from '../handleCaeChallenge';
import { getRetryAfterMs, isTransientError } from './shouldRetry';

const MAX_RETRY_ATTEMPTS = 3;

type CaeError = AxiosError & { isCaeChallenge?: boolean; claims?: string };

/**
 * Runs a Graph request with the same resilience the MIME read already has:
 * retries transient failures with backoff and recovers from CAE token
 * challenges. Permanent errors are re-thrown so callers can surface them.
 *
 * A move passes the folder it targets, the one signal that tells a not-found
 * item apart from a destination the mailbox doesn't have.
 */
export const withGraphRetry = async <T>(
  request: () => Promise<T>,
  {
    isSharedMailbox = false,
    destinationFolderId,
  }: { isSharedMailbox?: boolean; destinationFolderId?: string } = {},
): Promise<T> => {
  let attempt = 0;
  let caeRetried = false;

  for (;;) {
    try {
      // eslint-disable-next-line no-await-in-loop
      return await request();
    } catch (error) {
      const caeError = error as CaeError;

      if (caeError.isCaeChallenge && caeError.claims && !caeRetried) {
        // Recover from a CAE token challenge once, then loop to retry immediately.
        caeRetried = true;
        logInfo('CAE challenge detected - attempting token refresh');
        // eslint-disable-next-line no-await-in-loop
        const newToken = await handleCaeChallenge(caeError.claims, isSharedMailbox);

        if (!newToken) {
          throw error;
        }
      } else if (!isTransientError(caeError, destinationFolderId)) {
        throw error;
      } else {
        attempt += 1;

        if (attempt > MAX_RETRY_ATTEMPTS) {
          logError('Graph retries exhausted', caeError);
          throw error;
        }

        const delay = Math.max(getRetryDelay(attempt), getRetryAfterMs(caeError) ?? 0);
        logInfo(`Graph retry ${attempt}/${MAX_RETRY_ATTEMPTS} after ${delay}ms`);
        // eslint-disable-next-line no-await-in-loop
        await sleep(delay);
      }
    }
  }
};
